import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import { RegExpService } from "../../../../../util/common/reg-exp.service";
import { ToolTipSettings } from '../../tooltip/tooltip.util';
import {AllIconsComponent} from "../../all-icons/all-icons.component";
import {ButtonComponent} from "../../buttons/button.component";
import {TooltipComponent} from "../../tooltip/tooltip.component";
import {ButtonProps} from "../../buttons/button.util";
import {DEFAULT_DATE_RANGE} from "../input.util";
import {NgxMaskDirective} from "ngx-mask";

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => InputComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AllIconsComponent,
    FormsModule,
    ButtonComponent,
    TooltipComponent,
    NgxMaskDirective
  ]
})
export class InputComponent implements OnChanges, AfterViewInit, ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  private readonly regExpService = RegExpService;
  private regexStuff = new InputRegexStuff();
  /**
   * @output blur to transmit closed input value.
   * @output inputChange to transmit input value on change.
   */

  @Input() input: string | number;
  @Input() inputID = (Math.random() + 1).toString(36).substring(7);
  /**
   * Use type="email" only when handling two-way binding (input), but not with FormControl.
   * It is to avoid conflicts between form control and app-input 'email' type validations.
   */
  @Input() type = 'text';
  @Input() hideText = false;
  @Input() pointerOnToolTip = false;
  @Input() autofocus = false;
  @Input() autocomplete: 'on' | 'off' = 'on';
  /**
   * For some reason readonly is true be default (historically), but in some cases
   * we need to have an ability to configure it. For example, on login page
   * we have two inputs: email and password, and only when readonly is false ‼️ we can
   * make them both able for autocompleting by browser. Without it only password-input
   * suggest us autocomplete values.
   */
  @Input() readonly: boolean = true;

  @Input() prefixFirstIcon = ''; // First left icon, could be used with other classes. Eg. 'search.svg; main-8'"
  @Input() prefixSecondIcon = ''; // Second left icon, could be used with other classes. Eg. 'close.svg; black-9'"
  @Input() suffixFirstIcon = ''; //  First right icon, could be used with other classes. Eg. 'search.svg; black-10'"
  @Input() suffixSecondIcon = ''; //  Second right icon, could be used with other classes. Eg. 'search.svg; main-8'"

  @Input() buttonProps: ButtonProps;
  @Input() secondButtonProps: ButtonProps;
  @Input() firstSuffixImageTooltip = '';
  @Input() prefixText = '';
  @Input() suffixText = '';
  @Input() enterKeyText = '';
  @Input() isShrinked = false;

  @Input() minDate = DEFAULT_DATE_RANGE.minDate;
  @Input() maxDate = DEFAULT_DATE_RANGE.maxDate;
  @Input() maxLength: number;

  @Input() additionalClass: InputErrorClass = '';
  @Input() inputClass = ''; // Classes to be applied directly to the <input> tag, when it is not date input.

  @Input() separator = null;
  @Input() thousandSeparator = null;

  @Input() title = '';
  @Input() placeholder = '';
  @Input() textArea = false;
  @Input() dateInput = false;
  @Input() bottomText = '';

  @Input() allowNegativeNumbers = null;

  @Input() suffix = '';
  @Input() prefix = '';

  @Input() regularExpression = '';
  @Input() allowOnlyRegularExpression = true;

  @Input() checkZeroOnStart = false;
  @Input() required = false;

  @Output() inputChange = new EventEmitter<string>();
  @Output() blurEmitter = new EventEmitter<string | number>();
  @Output() focusEmitter = new EventEmitter<void>();
  @Output() hasError = new EventEmitter<boolean>();
  @Output() suffixFirstIconEmitter = new EventEmitter<void>();
  @Output() suffixSecondIconEmitter = new EventEmitter<void>();
  @Output() prefixFirstIconEmitter = new EventEmitter<void>();
  @Output() suffixFirstIconMouseEnterEmitter = new EventEmitter<void>();
  @Output() suffixFirstIconMouseLeaveEmitter = new EventEmitter<void>();
  @Output() prefixSecondIconEmitter = new EventEmitter<void>();
  @Output() suffixFirstIconEmitterWithEvent = new EventEmitter<string>();
  @Output() buttonClick = new EventEmitter<void>();
  @Output() secondButtonClick = new EventEmitter<void>();

  @ViewChild('inputElement') inputElement: ElementRef;

  disabled = false;
  firstSuffixTooltipSettings: ToolTipSettings = { show: false };

  ngAfterViewInit(): void {
    if (this.autofocus) {
      /**  Use setTimeout to delay the focus operation to the next JavaScript tick,
       preventing the modal from jumping to the bottom of the page.
       link to example {@link https://github.com/ng-bootstrap/ng-bootstrap/issues/1776}
       */
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.disabled = this.additionalClass === 'disabled';
    // Check email also here for trigger on program changing
    if (changes.input && this.type === 'email') {
      this.checkIfEmailIsValid(changes.input.currentValue);
    }
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  writeValue(value: any): void {
    this.input = value;
    /**
     * Calling onInputChange() here needs when we change the value by code, not by ui.
     * It invokes the same "on changing" reaction like when we change the value by ui.
     *
     * For example, we need to validate email input with regExp both when we change it like user
     * and when we change by method .setValue() in code. If it is not valid email, we need to
     * set additionalClasses to 'error'.
     */
    this.onInputChange(value, false);
    this.cdr.detectChanges();
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.additionalClass = 'disabled';
    } else if (this.additionalClass === 'disabled') {
      this.additionalClass = '';
    }
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouched = fn;
  }

  // eslint-disable-next-line
  onChange(value): void { }

  onTouched(): void { }

  /**
   * Pls see {@link readonly} for the historical reasoning
   *
   * The `if` condition was added in order to only apply the 5ms delay workaround to inputs that are actually readonly,
   * avoiding unnecessary delays for already editable inputs (in case you set `[readonly]="false"`), which reduces the
   * delay in input responses (especially when there is a lot of inputs in one table).
   */
  async disableAutocompleteOnFocus(): Promise<void> {
    if (this.readonly) {
      await new Promise((resolve) => setTimeout(resolve, 5));
      (this.inputElement.nativeElement as HTMLElement).removeAttribute('readonly');
    }
    this.focusEmitter.emit();
  }

  /**
   * Emits the value by input change => [(input)].
   * There are checks if input starts with zero or separator provides extra commas.
   * Also returns the result value.
   *
   * @param value is the raw string.
   * @param isInputChangeEmitterTriggered is a flag that allows to emit input change.
   * This flag must be used carefully as it might cause infinite recursion.
   */
  onInputChange(value: string, isInputChangeEmitterTriggered: boolean = true): string {
    const rawValue = this.returnRawValue(value);
    this.input = rawValue;
    if (isInputChangeEmitterTriggered) {
      this.onChange(rawValue);
    }
    if (rawValue !== undefined) {
      if (isInputChangeEmitterTriggered) {
        this.inputChange.emit(rawValue);
      }
      if (this.type === 'email') {
        this.checkIfEmailIsValid(rawValue);
      }
    }
    return rawValue;
  }

  onInputBlur(): void {
    const rawValue = this.input ? this.returnRawValue(String(this.input)) : this.input;
    this.blurEmitter.emit(rawValue);
  }

  showFirstSuffixTooltip(elementId: string): void {
    if (this.firstSuffixImageTooltip) {
      this.firstSuffixTooltipSettings = {
        show: true,
        coordinates: document.getElementById(elementId)?.getBoundingClientRect(),
        title: this.firstSuffixImageTooltip,
      };
    }
    this.suffixFirstIconMouseEnterEmitter.emit();
  }

  hideFirstSuffixTooltip(): void {
    this.firstSuffixTooltipSettings.show = false;
    this.suffixFirstIconMouseLeaveEmitter.emit();
  }

  private returnRawValue(value?: string): string {
    let rawValue = value ?? '';
    rawValue = this.checkNaN(rawValue);
    rawValue = this.checkZeroOnStartFoo(rawValue);
    rawValue = this.checkRegex(rawValue);
    rawValue = this.checkSeparator(rawValue);
    return rawValue;
  }

  private checkIfEmailIsValid(value: string): void {
    this.additionalClass = this.regExpService.email.test(value) ? '' : 'error';
  }

  private checkNaN(value: string): string {
    if (typeof value === 'number' && isNaN(value)) {
      return '';
    }
    return value;
  }

  private checkSeparator(value: string): string {
    if (this.thousandSeparator) {
      const regEx = new RegExp(this.thousandSeparator, 'g');
      value = (value)?.replace(regEx, '');
    }
    return value;
  }

  private checkRegex(value: string): string {
    value = String(value);
    if (!this.regularExpression) {
      return value;
    }

    /**
     * We initialize regex stuff here and not in ngOn(After)Init()/constructor, because
     * ngOnChanges() event, which calls this checkRegex func, starts earlier than others, but
     * we already need some stuff here
     */
    if (!this.regexStuff.regexStuffInitialized) {
      this.regexStuff.initRegexStuff(this.regularExpression);
    }

    if (this.allowOnlyRegularExpression) {
      // common characters from copy-paste, that must be eliminated in case they are not allowed in the regex.
      if (!this.regexStuff.regexWithoutBoundCharacters.includes('$')) {
        value = value.replace(/\$/g, '');
      }
      if (!this.regexStuff.regexWithoutBoundCharacters.includes(' ')) {
        value = value.replace(/ /g, '');
      }

      // If a value has a length of 1 and does not match a value, valueAfterRegex is null.
      // Otherwise, on the position 0 it has the same || the most similar value based on regex.
      const valueAfterRegex = value.match(this.regexStuff.regex);
      value = valueAfterRegex ? valueAfterRegex.join('') : '';
      if (this.inputElement) {
        this.inputElement.nativeElement.value = value;
      }
    }

    if (!InputComponent.matchExact(this.regularExpression, value)) {
      this.additionalClass = 'error';
      this.hasError.emit(true);
    } else {
      this.additionalClass = '';
      this.hasError.emit(false);
    }

    return value;
  }

  private checkZeroOnStartFoo(value: string): string {
    if (!this.checkZeroOnStart) {
      return value;
    }
    if (value.length > 1 &&
      value.charAt(0) === '0' && value.charAt(1) !== '.') {
      return '0.' + value.substring(1);
    }
    return value;
  }

  private static matchExact(r, str): boolean {
    const match = str.match(r);
    return match && str === match[0];
  }
}

export type InputErrorClass = 'disabled' | 'error' | 'error-for-disabled' | 'blue-border' | '';

class InputRegexStuff {
  regexStuffInitialized: boolean = false;
  regexWithoutBoundCharacters: string = '';
  regex: RegExp;

  initRegexStuff(regexStr: string): void {
    this.regexWithoutBoundCharacters = regexStr.charAt(regexStr.length - 1) === '$' ?
      regexStr.substring(1, regexStr.length - 1) : regexStr;
    this.regex = new RegExp(this.regexWithoutBoundCharacters, 'g');
    this.regexStuffInitialized = true;
  }
}
